import { createSlice } from "@reduxjs/toolkit";
import { getMe } from "../apiActions/authActions";
import adminRoutes from "../../routes/adminRoutes";
import { clearAuthorizationHeader } from "../../api/axiosClient";

interface AuthState {
  loading: boolean;
  error: null | string;
  isAuthenticated: boolean;
  user: User | null;
  homeRoute: string;
  userType: string;
  maintenance: boolean;
}

type ATC = {
  name: string;
  id: string;
  created_at: string;
};

type User = {
  id: string;
  created_at: string;
  username: string;
  email: string;
  permissions: string[];
  student_id: string;
  atc: ATC;
  banned: boolean;
  hidden: boolean;
};

const initialState: AuthState = {
  loading: true,
  error: null,
  isAuthenticated: localStorage.getItem("token") ? true : false,
  user: null,
  homeRoute: "/home",
  userType: "",
  maintenance: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      state.isAuthenticated = false;
      state.user = null;
      state.userType = "";
      // Drop the token and the axios default header together, so no stale token is
      // sent after logout.
      clearAuthorizationHeader();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMe.pending, (state: AuthState) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMe.fulfilled, (state: AuthState, action) => {
        state.loading = false;
        state.error = null;
        state.user = action.payload;
        state.isAuthenticated = true;
        const permissions = state.user?.permissions;

        if (permissions?.length) {
          if (permissions.includes("admin")) {
            state.userType = "admin";
            for (const { path, permission, hidden } of adminRoutes) {
              if (!hidden && permission && permissions?.includes(permission)) {
                state.homeRoute = `/${path}`;
                break;
              }
            }
          } else if (
            permissions.length === 1 &&
            permissions.includes("student")
          ) {
            state.userType = "student";
            state.homeRoute = `/schedule-session`;
            state.maintenance = action.payload.enabled;
          } else if (
            permissions.includes("atc.edit") ||
            permissions.includes("atc.view")
          ) {
            state.userType = "atc";
            state.maintenance = action.payload.enabled;
          } else {
            state.userType = "admin";
            for (const { path, permission, hidden } of adminRoutes) {
              if (!hidden && permission && permissions?.includes(permission)) {
                state.homeRoute = `/${path}`;
                break;
              }
            }
          }
        }
      })
      .addCase(getMe.rejected, (state: AuthState, action) => {
        state.loading = false;
        // A rejection from a superseded token (the deep-link handoff replaces the
        // session mid-flight) must not clear storage or bounce to /login — doing so
        // used to throw away the session that was just minted for the exam.
        if ((action?.payload as any)?.stale) return;
        if ((action?.payload as any)?.description === "invalid auth") {
          state.isAuthenticated = false;
          localStorage.clear();
          state.userType = "";
          window.location.href = "/login";
        }
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
